(ns renolit-roofing-service.view.laboratory-report
  (:require [malli.util :as mu]
            [reagent.core :as r]
            [re-frame.core :as rf]
            [renolit-roofing-service.config :as config]
            [renolit-roofing-service.layout.components :refer [collapsable-section]]
            [renolit-roofing-service.layout.display-item :as layout.display-item]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.layout.form :as layout.form]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.tooltip :as tooltip]
            [renolit-roofing-service.tooltip.generic-popup :as tooltip.generic-popup]
            [renolit-roofing-service.view :as view]
            [renolit-roofing-service.view.installer-add-observation-popup :as view.add-observation-popup]
            [renolit-roofing-service.view.installer-service-information.zone :as view.zone]
            [renolit-roofing-service.view.laboratory-sample-analysis :as view.lab-sample-analysis]
            ["@vis.gl/react-google-maps" :refer [APIProvider]]))

(def path-params-schema
  [:map
   [:id
    [:uuid]]])

(def ^:const route-config
  {:name [:laboratory-report]
   :auth-req? true
   :parameters {:path path-params-schema}
   :controllers [{:parameters {:path (mu/keys path-params-schema)}
                  :start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 (fn [_ [_ opts]]
   (let [service-id (get-in opts [:path :id])]
     {:fx [[:dispatch [::srv.service/fetch-service-by-id service-id
                       {:on-success-evt [::service-loaded]}]]]})))

(rf/reg-event-fx
 ::service-loaded
 (fn [{:keys [db]} [_ service]]
   (let [conclusions (or (:conclusions service) {})]
     {:db (-> db
              (assoc :lab-report/conclusions conclusions)
              (assoc :lab-report/wp-filter nil))
      :fx [[:dispatch [::view.zone/initialize-zones-map]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [{:keys [db]} _]
   {:db (dissoc db :lab-report/wp-filter
                :lab-report/selected-observation
                :lab-report/conclusions
                :lab-report/saving?
                :lab/presigned-urls
                :zones
                :zones-bounds
                :default-map-viewport
                :gmaps/read-only?
                :gmaps/bridge-registry)
    :fx [[:dispatch [::srv.service/clear-service]]]}))

(rf/reg-event-db
 ::update-conclusion
 (fn [db [_ key value]]
   (assoc-in db [:lab-report/conclusions key] value)))

(rf/reg-event-db
 ::update-observation-comment
 (fn [db [_ observation-id comment]]
   (let [comments (or (get-in db [:lab-report/conclusions :comments]) [])
         idx (some (fn [[i c]] (when (= (:observation-id c) observation-id) i))
                   (map-indexed vector comments))
         entry {:observation-id observation-id :comment comment}
         updated (if idx
                   (assoc comments idx entry)
                   (conj comments entry))]
     (assoc-in db [:lab-report/conclusions :comments] updated))))

(rf/reg-event-fx
 ::save-report
 (fn [{:keys [db]} [_ service-id]]
   (let [conclusions (-> (get db :lab-report/conclusions {})
                         (update :comments #(or % []))
                         (update :visual-inspection #(when (seq %) %))
                         (update :non-destructive-testing #(when (seq %) %))
                         (update :destructive-testing #(when (seq %) %)))]
     {:db (assoc db :lab-report/saving? true)
      :fx [[:dispatch [::srv.service/save-service-conclusions
                       service-id
                       conclusions
                       {:on-success-evt [::save-report-success]
                        :on-failure-evt [::save-report-failure]}]]]})))

(rf/reg-event-db
 ::save-report-success
 (fn [db _]
   (assoc db :lab-report/saving? false)))

(rf/reg-event-db
 ::save-report-failure
 (fn [db _]
   (assoc db :lab-report/saving? false)))

(rf/reg-event-fx
 ::switch-to-report
 (fn [{:keys [db]} _]
   (let [service-id (get-in db [:current-service :id])
         conclusions (-> (get db :lab-report/conclusions {})
                         (update :comments #(or % []))
                         (update :visual-inspection #(when (seq %) %))
                         (update :non-destructive-testing #(when (seq %) %))
                         (update :destructive-testing #(when (seq %) %)))]
     {:db (assoc db :lab-report/saving? true)
      :fx [[:dispatch [::srv.service/save-service-conclusions
                       service-id
                       conclusions
                       {:on-success-evt [::submit-report-success service-id]
                        :on-failure-evt [::submit-report-failure]}]]]})))

(rf/reg-event-fx
 ::submit-report-success
 (fn [{:keys [db]} [_ service-id]]
   {:db (assoc db :lab-report/saving? false)
    :fx [[:dispatch [::nav/push-state [:laboratory-report-summary] {:id service-id}]]]}))

(rf/reg-event-fx
 ::fetch-presigned-url
 (fn [{:keys [db]} [_ service-id object-id]]
   (if-not (get-in db [:lab/presigned-urls object-id])
     {:fx [[:dispatch [::view.lab-sample-analysis/fetch-read-presigned-url service-id object-id]]]}
     {})))

(rf/reg-event-db
 ::submit-report-failure
 (fn [db _]
   (assoc db :lab-report/saving? false)))

(rf/reg-sub
 ::wp-filter
 (fn [db _]
   (get db :lab-report/wp-filter)))

(rf/reg-event-db
 ::set-wp-filter
 (fn [db [_ wp]]
   (assoc db :lab-report/wp-filter wp)))

(rf/reg-sub
 ::conclusions
 (fn [db _]
   (get db :lab-report/conclusions {})))

(rf/reg-sub
 ::comments-by-observation
 :<- [::conclusions]
 (fn [conclusions _]
   (let [comments (or (:comments conclusions) [])]
     (into {} (map (juxt :observation-id :comment)) comments))))

(rf/reg-sub
 ::saving?
 (fn [db _]
   (get db :lab-report/saving? false)))

(rf/reg-sub
 ::zone-name-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [zones (or (:zones service) [])]
     (into {} (map (juxt :id :name)) zones))))

(rf/reg-sub
 ::review-index
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [reviews (or (:reviews service) [])]
     (into {} (map (juxt :id identity)) reviews))))

(rf/reg-sub
 ::all-observations
 :<- [::srv.service/current-service]
 :<- [::zone-name-index]
 :<- [::review-index]
 (fn [[service zone-names review-index] _]
   (let [observations (or (:observations service) [])]
     (->> observations
          (remove :clear)
          (mapv (fn [obs]
                  (let [review (get review-index (:review-id obs))
                        zone-id (:zone-id review)
                        zone-name (get zone-names zone-id "")]
                    (assoc obs
                           :zone-name zone-name
                           :review-type (:type review)
                           :wp-type (:wp-type review)))))))))

(rf/reg-sub
 ::filtered-observations
 :<- [::all-observations]
 :<- [::wp-filter]
 (fn [[observations wp-filter] _]
   (if wp-filter
     (filterv #(= (keyword (:wp %)) wp-filter) observations)
     observations)))

(rf/reg-sub
 ::wp-counts
 :<- [::all-observations]
 (fn [observations _]
   (frequencies (map (comp keyword :wp) observations))))

(rf/reg-sub
 ::observations-by-wp
 :<- [::filtered-observations]
 (fn [observations _]
   (group-by (comp keyword :wp) observations)))

(rf/reg-sub
 ::visual-inspection-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :visual-inspection [])))

(rf/reg-sub
 ::non-destructive-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :non-destructive-testing [])))

(rf/reg-sub
 ::destructive-observations
 :<- [::observations-by-wp]
 (fn [by-wp _]
   (get by-wp :destructive-testing [])))

(rf/reg-sub
 ::get-analysis-by-image-id
 :<- [::srv.service/current-service]
 (fn [service [_ image-id]]
   (when image-id
     (let [analyses (or (:analyses service) [])]
       (first (filter #(= image-id (:image-id %)) analyses))))))

(rf/reg-sub
 ::get-obs-image-by-image-id
 :<- [::srv.service/current-service]
 (fn [service [_ observation-id image-id]]
   (when (and observation-id image-id)
     (let [observation (first (filter #(= observation-id (:id %)) (:observations service)))]
       (first (filter #(= image-id (:object-id %)) (:images observation)))))))

(def ^:const observation-popup-id "lab-report-observation-popup")

(defn field-value-view
  [label-transl-kw value]
  [layout.display-item/main
   {:type :text
    :value value
    :label-transl-kw label-transl-kw}])

(defn image-thumbnail
  [state url photo-id object-id]
  [:div.add-observation-popup__thumbnail
   {:on-click
    (fn []
      (swap! state #(assoc % :obs-img-selected-id object-id)))}
   [:img {:src url}]
   [:div.add-observation-popup__thumbnail-overlay
    [:span.add-observation-popup__thumbnail-id
     photo-id]]])

(defn image-analysis
  [state obs-img-selected-id]
  (let [analysis @(rf/subscribe [::get-analysis-by-image-id obs-img-selected-id])
        presigned-urls @(rf/subscribe [::view.lab-sample-analysis/presigned-urls])
        url (get presigned-urls obs-img-selected-id)]
    (when analysis
      (when-not (:analysis @state)
        (swap! state (fn [state] (assoc state :analysis analysis))))
      [:div.service-section.service-section--half
       [:div.service-section
        [:img {:style {:object-fit "contain" :height "100%" :width "100%"} :src url}]]
       [:div.service-section
        [:div.ls-card__content
         [:div.ls-form
          [layout.form/item
           {:context :lab-sample-analysis
            :type :radio-button-group
            :state state
            :path [:analysis :valid]
            :disabled? true
            :label (t [:page.laboratory.sample-analysis/photo-valid])
            :choices [{:id true :name (t [:page.laboratory.sample-analysis/yes])}
                      {:id false :name (t [:page.laboratory.sample-analysis/no])}]}]
          [layout.form/item
           {:context :lab-sample-analysis
            :type :input
            :state state
            :path [:analysis :crack-level]
            :schema [:int {:min 1 :max 9}]
            :read-only? true
            :disabled? true
            :label (t [:page.laboratory.sample-analysis/crack-level])
            :input-type "number"
            :input-opts {:min 1 :max 9}}]
          [layout.form/item
           {:context :lab-sample-analysis
            :type :text-area
            :state state
            :read-only? true
            :disabled? true
            :path [:analysis :observations]
            :label (t [:page.laboratory.sample-analysis/observations])}]
          [layout.form/item
           {:context :lab-sample-analysis
            :type :checkbox
            :state state
            :disabled? true
            :path [:analysis :request-repetition]
            :label (t [:page.laboratory.sample-analysis/request-repetition])}]]]]])))

(defn- observation-details
  [state observation]
  (let [{:keys [description incidence-level images]} observation
        incidence-label (when incidence-level
                          (t [(keyword (str "domain.choices.incidence-level/"
                                            (name incidence-level)))]))
        obs-type (:observation observation)
        {:keys [google-maps-api-key]} @(rf/subscribe [::config/config-data])
        presigned-urls @(rf/subscribe [::view.lab-sample-analysis/presigned-urls])]
    [:div.service-section.service-section--half
     [:div.service-section
      [:> APIProvider {:apiKey google-maps-api-key}
       [view.add-observation-popup/observation-map state {:height "300px" :width "100%" :border-radius "8px"}]]]
     [:div.service-section
      (when obs-type
        [field-value-view :domain.service.observation/observation obs-type])
      (when incidence-label
        [field-value-view :domain.service.observation/incidence-level incidence-label])
      (when (and description (not-empty description))
        [field-value-view :domain.service.observation/description description])
      (when (seq images)
        [:div.add-observation-popup__thumbnails
         (for [{:keys [object-id photo-id]} images]
           (let [url (get presigned-urls object-id)]
             (when url
               ^{:key photo-id}
               [image-thumbnail state url photo-id object-id])))])]]))

(defn- observation-popup-content
  [{:keys [_observation]}]
  (let [state (r/atom {:obs-img-selected-id nil
                       :analysis nil})]
    (fn [{:keys [observation]}]
      (let [{:keys [id]} observation
            obs-img-selected-id (:obs-img-selected-id @state)
            {:keys [photo-id]} @(rf/subscribe [::get-obs-image-by-image-id id obs-img-selected-id])
            obs-title (or (:ui-id observation) (str "I" (subs (str id) 0 8)))
            img-title (str "Photo ID: " photo-id)]
        [:<>
         [tooltip.generic-popup/generic-popup-header
          {:title (if-not obs-img-selected-id obs-title img-title)
           :icon (when obs-img-selected-id [layout.icon/icon
                                            {:key :back
                                             :clickable? true
                                             :props {:on-click
                                                     (fn []
                                                       (swap! state #(assoc % :obs-img-selected-id nil)))}}])
           :popup-id observation-popup-id}]
         [:div.lr-observation-popup
          [:div.lr-observation-popup__body
           (if-not obs-img-selected-id
             [observation-details state observation]
             [image-analysis state obs-img-selected-id])]]
         [tooltip.generic-popup/generic-popup-footer
          {:popup-id observation-popup-id
           :on-cancel {:prevent-default? true
                       :fn #(rf/dispatch [::close-observation-popup])}}]]))))

(rf/reg-event-fx
 ::open-observation-popup
 (fn [{:keys [db]} [_ observation]]
   (let [service (get db :current-service)]
     {:fx (into [[:dispatch [::tooltip/register
                             {:id observation-popup-id
                              :component observation-popup-content
                              :argv [{:observation observation}]
                              :class ["generic-popup-standard"]}]]]
                (map (fn [image]
                       [:dispatch [::fetch-presigned-url (:id service) (:object-id image)]])
                     (:images observation)))})))

(rf/reg-event-fx
 ::close-observation-popup
 (fn [_ _]
   {:fx [[:dispatch [::tooltip/destroy-by-id {:id observation-popup-id}]]]}))

(defn- format-time
  [date]
  (when date
    (let [d (if (inst? date) date (js/Date. date))
          h (.getHours d)
          m (.getMinutes d)]
      (str (when (< h 10) "0") h ":" (when (< m 10) "0") m "h"))))

(defn- truncated-obs-id
  [obs]
  (or (:ui-id obs) (let [s (str (:id obs))]
                     (str "I" (subs s 0 (min 8 (count s)))))))

(defn- observation-card
  "Observation card for the split-panel right side list."
  [{:keys [id wp wp-type zone-name task-type created-at] :as obs}
   {:keys [on-click]}]
  (let [wp-type-label (when (and wp-type
                                 (not= wp-type :nil)
                                 (not= (name wp-type) "nil"))
                        (t [(keyword (str "domain.choices.wp-type/"
                                          (name wp-type)))]))
        wp-label (when wp
                   (t [(keyword (str "domain.choices.wp/" (name wp)))]))
        task-type-label (when task-type
                          (t [(keyword (str "domain.choices.task-type/"
                                            (name task-type)))]))
        id-short (truncated-obs-id obs)]
    [:div.review-card
     {:on-click (or on-click
                    #(rf/dispatch [::open-observation-popup obs]))}
     [:div.review-card__header
      [:div.review-card__title-row
       [layout.icon/icon {:key :info}]
       [:span.review-card__id id-short]]
      (when created-at
        [:span.review-card__time (format-time created-at)])]
     [:div.review-card__body
      [:p.review-card__wp-type (or wp-type-label wp-label)]
      (when (seq zone-name)
        [:p.review-card__zone zone-name])]
     [:div.review-card__tags
      (when task-type-label
        [:span.review-card__tag task-type-label])
      (when wp-type-label
        [:span.review-card__tag wp-type-label])]]))

(defn- observation-card-with-comment
  "Observation card with comment textarea, used in accordion sections."
  [_obs]
  (let [comments-index (rf/subscribe [::comments-by-observation])]
    (fn [obs]
      (let [obs-id (:id obs)
            comment-text (or (get @comments-index obs-id) "")]
        [:div.lr-accordion-card
         [:div.lr-accordion-card__obs
          [observation-card obs {}]]
         [:div.lr-accordion-card__comment
          [:textarea.lr-accordion-card__textarea
           {:value comment-text
            :rows 2
            :placeholder (t [:page.laboratory.report/comment-placeholder])
            :on-change #(rf/dispatch
                         [::update-observation-comment
                          obs-id
                          (.. % -target -value)])}]]]))))

(defn- zone-map-panel
  "Left panel: zone map with focused/general review buttons."
  []
  [:div.lr-map-panel
   [view.zone/zones-map-wrapper]])

(defn- observations-right-panel
  "Right panel: scrollable list of observation cards."
  []
  (let [observations (rf/subscribe [::filtered-observations])]
    (fn []
      [:div.lr-observations-panel
       [:div.lr-observations-panel__sort
        [:span (t [:page.laboratory.report/sort-chronologically])]]
       [:div.lr-observations-panel__list
        (if (seq @observations)
          (doall
           (for [obs (sort-by :created-at @observations)]
             ^{:key (:id obs)}
             [observation-card obs {}]))
          [:div.empty-state
           [:p (t [:common/no-data])]])]])))

(defn- split-panel
  "Split panel with zone map on left and observations on right."
  []
  [:div.lr-split-panel
   [:div.lr-split-panel__left
    [zone-map-panel]]
   [:div.lr-split-panel__right
    [observations-right-panel]]])

(defn- accordion-observation-list
  "List of observation cards with comment textareas for an accordion."
  [observations]
  [:div.lr-accordion-list
   (if (seq observations)
     (doall
      (for [obs observations]
        ^{:key (:id obs)}
        [observation-card-with-comment obs]))
     [:div.empty-state
      [:p (t [:common/no-data])]])])

(defn- visual-inspection-section
  []
  (let [observations (rf/subscribe [::visual-inspection-observations])]
    (fn []
      [collapsable-section
       {:title (t [:page.laboratory.report/visual-inspection])
        :start-open false}
       [accordion-observation-list @observations]])))

(defn- non-destructive-section
  []
  (let [observations (rf/subscribe [::non-destructive-observations])]
    (fn []
      [collapsable-section
       {:title (t [:page.laboratory.report/non-destructive])
        :start-open false}
       [accordion-observation-list @observations]])))

(defn- destructive-section
  []
  (let [observations (rf/subscribe [::destructive-observations])]
    (fn []
      [collapsable-section
       {:title (t [:page.laboratory.report/destructive])
        :start-open false}
       [accordion-observation-list @observations]])))

(defn- conclusion-visit-program-item
  "Checkbox + textarea for one WP in the conclusion."
  [_wp-key]
  (let [conclusions (rf/subscribe [::conclusions])]
    (fn [wp-key]
      (let [wp-label (t [(keyword (str "domain.choices.wp/" (name wp-key)))])
            value (get @conclusions wp-key)
            checked? (some? value)
            input-id (str "conclusion-wp-" (name wp-key))]
        [:div.lr-conclusion__visit-item
         [:div.lr-conclusion__visit-checkbox
          [:input {:type "checkbox"
                   :id input-id
                   :checked checked?
                   :on-change #(rf/dispatch
                                [::update-conclusion
                                 wp-key
                                 (if checked? nil "")])}]
          [:label {:htmlFor input-id} wp-label]]
         (when checked?
           [:textarea.lr-conclusion__visit-textarea
            {:value (or value "")
             :rows 2
             :on-change #(rf/dispatch
                          [::update-conclusion
                           wp-key
                           (.. % -target -value)])}])]))))

(defn- conclusion-section
  []
  (let [conclusions (rf/subscribe [::conclusions])
        wp-keys [:visual-inspection :non-destructive-testing :destructive-testing]]
    (fn []
      (let [membrane-life (or (get @conclusions :membrane-life) "")
            final (or (get @conclusions :final) "")]
        [collapsable-section
         {:title (t [:page.laboratory.report/conclusion])
          :start-open true}
         [:div.lr-conclusion
          [:div.lr-conclusion__visit-programs
           [:h4 (t [:page.laboratory.report/conclusion-per-visit])]
           (doall
            (for [wp-key wp-keys]
              ^{:key wp-key}
              [conclusion-visit-program-item wp-key]))]
          [:div.lr-conclusion__membrane-years
           [:label (t [:page.laboratory.report/membrane-years])]
           [:input.lr-conclusion__number-input
            {:type "number"
             :min 0
             :value membrane-life
             :on-change #(let [v (.. % -target -value)]
                           (rf/dispatch
                            [::update-conclusion
                             :membrane-life
                             (if (seq v) (js/parseInt v 10) nil)]))}]]
          [:div.lr-conclusion__final
           [:label (t [:page.laboratory.report/final-conclusion])]
           [:textarea.lr-conclusion__textarea
            {:value final
             :rows 4
             :on-change #(rf/dispatch
                          [::update-conclusion
                           :final
                           (.. % -target -value)])}]]]]))))

(defn- header
  "Page header with back link, title, save and report buttons."
  [_service-id _service-name]
  (let [saving? (rf/subscribe [::saving?])]
    (fn [service-id service-name]
      [:div.lr-header
       [:a.back-link {:href (nav/href [:laboratory-home])}
        [layout.icon/icon {:key :back}]
        (t [:page.laboratory.report/back-to-list])]
       [:div.lr-header__title-row
        [:h3 (or service-name "")]
        [:div.lr-header__actions
         [:button.btn.btn--secondary
          {:disabled @saving?
           :on-click #(rf/dispatch [::save-report service-id])}
          (if @saving?
            [:span.lr-header__btn-spinner]
            (t [:page.laboratory.report/save]))]
         [:button.btn.btn--primary
          {:disabled @saving?
           :on-click #(rf/dispatch [::switch-to-report])}
          (t [:page.laboratory.report/send-to-rae])]]]])))

(defn- navigation-links
  "Links to project info and service info."
  [project-id service-id]
  [:div.lr-nav-links
   [:div.back-link
    [:a
     {:href (nav/href [:project] {:project-id project-id})}
     (t [:page.laboratory.report/go-to-project])]
    [layout.icon/icon {:key :right-arrow}]]
   [:div.back-link
    [:a
     {:href (nav/href [:service-details] {:service-id service-id})}
     (t [:page.laboratory.report/go-to-service])]
    [layout.icon/icon {:key :right-arrow}]]])

(def ^:private wp-order
  [:visual-inspection :non-destructive-testing :destructive-testing])

(defn- filter-bar
  [project-id service-id]
  (let [wp-filter @(rf/subscribe [::wp-filter])
        wp-counts @(rf/subscribe [::wp-counts])]
    [:div.lr-filter-bar
     [:div.tab-bar.tab-bar--pill
      (for [wp-key wp-order]
        (let [count   (get wp-counts wp-key 0)
              active? (= wp-filter wp-key)
              label   (t [(keyword (str "domain.choices.wp/" (name wp-key)))])]
          ^{:key (name wp-key)}
          [:div.tab-bar__tab
           {:class    (when active? "tab-bar__tab--selected")
            :on-click #(rf/dispatch [::set-wp-filter (when-not active? wp-key)])}
           (str label " (" count ")")]))]
     [:div.lr-filter-bar__right
      [navigation-links project-id service-id]]]))


(defn- map-mode-view
  "Map mode: split panel + accordion sections."
  []
  [:div.lr-map-mode
   [split-panel]
   [visual-inspection-section]
   [non-destructive-section]
   [destructive-section]
   [conclusion-section]])

(defn- main
  []
  (let [service @(rf/subscribe [::srv.service/current-service])
        service-id (:id service)
        project-id (:project-id service)
        service-name (:name service)]
    [:div.user_layout
     [:div.lab-report
      (if service
        [:<>
         [header service-id service-name]
         [filter-bar project-id service-id]
         [tooltip.generic-popup/main observation-popup-id]
         [map-mode-view]]
        [:div.loading (t [:common.loading])])]]))

(defmethod view/view-display (:name route-config)
  [_]
  [main])
